/**
 * useRecentlyChanged — boolean hook for "did this entity just change?"
 *
 * Pairs with the BrowserBus data_changed stream: when a server-side
 * mutation broadcasts {entity_type, entity_id, ...}, this hook lets a
 * renderer light up the corresponding row/cell for a brief window so
 * the user can SEE that the AI's work just landed there.
 *
 * Without this, every Tier-1A broadcast we shipped fires silently —
 * the page refetches but the user has no visual signal which row
 * actually moved. With it, a 1px emerald ring fades in for ~1500ms
 * on the affected entity, then fades out.
 *
 * Usage in a renderer:
 *
 *   const recent = useRecentlyChanged("contact", contact.id);
 *   <div className={`... transition-all duration-700 ${
 *     recent ? "ring-2 ring-emerald-300/70" : "ring-2 ring-transparent"
 *   }`}>
 *
 * Wildcards: pass entity_id=undefined to subscribe to "any change of
 * this entity_type." Useful for list-level "something just landed
 * here" indicators (e.g. the contacts-index getting a new row).
 *
 * Architecture: a module-level Map<key, ts> persists across renders
 * and component instances so a row freshly mounted post-broadcast
 * still flashes (the data refetched, the row appeared, the row's
 * useEffect runs, finds a recent timestamp in the registry, sets
 * state, schedules a clear). recordRecentChange is called from
 * useBrowserBus.ts on every data_changed event so the registry stays
 * in sync without each consumer needing to wire up listeners.
 *
 * Window: 1500ms. Long enough to catch the eye, short enough that a
 * staffer scrolling past doesn't see stale flashes from minutes ago.
 */

import { useEffect, useState } from "react";

const RECENT_WINDOW_MS = 1500;

interface RecordedEvent {
  entity_type: string;
  entity_id?: string;
  workspace_id?: string;
}

// Key = `${entity_type}:${entity_id ?? "*"}` → most-recent ts (ms)
const recentChanges = new Map<string, number>();

/**
 * Called by useBrowserBus.ts on every data_changed event so the
 * registry stays in sync. Not called by consumers directly.
 */
export function recordRecentChange(ev: RecordedEvent): void {
  if (!ev.entity_type) return;
  const ts = Date.now();
  // Both a specific-id key and a wildcard key per type, so consumers
  // can subscribe to either granularity.
  if (ev.entity_id) recentChanges.set(`${ev.entity_type}:${ev.entity_id}`, ts);
  recentChanges.set(`${ev.entity_type}:*`, ts);
}

/**
 * Returns true if a data_changed event matching (entity_type, entity_id)
 * has fired in the last RECENT_WINDOW_MS milliseconds. Re-renders the
 * component when an event fires AND when the window expires.
 */
export function useRecentlyChanged(
  entity_type: string,
  entity_id?: string | null
): boolean {
  const key = `${entity_type}:${entity_id ?? "*"}`;
  const [, setTick] = useState(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<RecordedEvent>;
      const ev = ce.detail;
      if (!ev || ev.entity_type !== entity_type) return;
      // entity_id filter: if the consumer pinned a specific id, only
      // re-render for that id. If they didn't, any event of this type
      // matters.
      if (entity_id != null && ev.entity_id !== entity_id) return;
      setTick((t) => t + 1);
      // Schedule a re-render at the window boundary so the className
      // can switch back to "no flash" without manual cleanup.
      const t = window.setTimeout(() => setTick((tt) => tt + 1), RECENT_WINDOW_MS + 50);
      return () => window.clearTimeout(t);
    };
    window.addEventListener("cmcp:browser-bus-data-changed", handler as EventListener);
    return () => {
      window.removeEventListener("cmcp:browser-bus-data-changed", handler as EventListener);
    };
  }, [entity_type, entity_id]);

  const last = recentChanges.get(key);
  if (last == null) return false;
  return Date.now() - last < RECENT_WINDOW_MS;
}
